import axios, { type AxiosError } from "axios";

/** Standard backend error payload returned by API endpoints. */
export interface BackendErrorPayload {
  errorCode: number;
  errorMessage: string;
  errorType: string;
  metadata: Record<string, string | number | boolean | null>;
  referenceId: string;
}

/** Axios error typed with the backend error payload. */
export type BackendAxiosError = AxiosError<BackendErrorPayload>;

const isBackendErrorPayload = (
  value: unknown,
): value is BackendErrorPayload => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const payload = value as Record<string, unknown>;

  return (
    typeof payload.errorCode === "number" &&
    typeof payload.errorMessage === "string" &&
    typeof payload.errorType === "string" &&
    typeof payload.referenceId === "string"
  );
};

/** Returns the backend error payload from an Axios error, if present. */
export const getBackendErrorPayload = (
  error: BackendAxiosError | null | undefined,
): BackendErrorPayload | null => {
  return error?.response?.data ?? null;
};

/** Returns the backend error message from an Axios error, if present. */
export const getBackendErrorMessage = (
  error: BackendAxiosError | null | undefined,
): string | null => {
  return getBackendErrorPayload(error)?.errorMessage ?? null;
};

/** Returns the backend error payload from an unknown error, if present. */
export const getBackendErrorPayloadFromUnknown = (
  error: unknown,
): BackendErrorPayload | null => {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const payload = error.response?.data;
  return isBackendErrorPayload(payload) ? payload : null;
};

/** Returns the backend error message from an unknown error, if present. */
export const getBackendErrorMessageFromUnknown = (
  error: unknown,
): string | null => {
  return getBackendErrorPayloadFromUnknown(error)?.errorMessage ?? null;
};
